import { ReactElement, ReactNode } from 'react';
import { EditorProps } from './types';
import { CellDropdown } from './CellDropdown';
import ReactSelect from 'react-select';
import { Dummy, selectProps } from './selectEditor';
import { CheckboxOption } from 'components/Select';
import { controlLabel, editorChoice, editorContainer } from './styles.css';
import { Box, Button } from '@ff-it/ui';

type SelectProps<V> = {
  options: V[];
  value: V[];
  getOptionLabel: (v: any) => string;
  getOptionValue: (v: any) => string;
  formatOptionLabel?: (v: any) => ReactNode;
  onSelect: (v: V) => void;
  onDeselect: (v: V) => void;
};

type EditProps<V> = {
  // items carry their index into the full value list, not this (custom-only) partition
  items: { item: V; index: number }[];
  getOptionLabel: (v: any) => string;
  formatOptionLabel?: (v: any) => ReactNode;
  getOptionValue: (v: any) => string;
  onAdd: () => void;
  onUpdate: (index: number, v: V) => void;
  autoFocus?: boolean;
};

type EditableChoiceEditorProps<V, Row, TSummaryRow> = Pick<EditorProps<Row, TSummaryRow>, 'onClose'> &
  SelectProps<V> &
  Pick<EditProps<V>, 'onAdd' | 'onUpdate'>;

function Select<V>({ onSelect, onDeselect, ...props }: SelectProps<V>): ReactNode {
  return (
    <ReactSelect
      {...props}
      {...selectProps}
      onChange={(_v, action: any) => {
        switch (action.action) {
          case 'deselect-option':
            onDeselect(action.option);
            return;
          case 'select-option':
            onSelect(action.option);
            return;
          default:
            throw new Error();
        }
      }}
      isMulti
      autoFocus
      components={{
        IndicatorsContainer: Dummy,
        Option: CheckboxOption,
      }}
    />
  );
}

function Edit<V>({
  items,
  onAdd,
  onUpdate,
  getOptionLabel,
  //   getOptionValue,
  formatOptionLabel = getOptionLabel,
  autoFocus,
}: EditProps<V>): ReactNode {
  return (
    <Box className={editorContainer}>
      <Box className={controlLabel}>Edit values</Box>
      {items.map(({ item, index }) => (
        <Box key={index} display="flex" alignItems="center" className={editorChoice} role="option">
          <Box flexGrow={1}>{formatOptionLabel(item)}</Box>
          {/* getOptionValue ? */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUpdate(index, item)}
            testId="update-option"
            icon="pencil"
          />
        </Box>
      ))}
      <div className="p-1">
        <Button size="xs" variant="primary" onClick={onAdd} testId="add-option" autoFocus={autoFocus} icon="plus">
          Add value
        </Button>
      </div>
    </Box>
  );
}

export function EditableChoiceEditor<V, Row, TSummaryRow>({
  value,
  options,
  onClose,
  onSelect,
  onAdd,
  onUpdate,
  ...common
}: EditableChoiceEditorProps<V, Row, TSummaryRow>): ReactElement {
  const selectValue: V[] = [];
  const editItems: { item: V; index: number }[] = [];

  // partition values, keeping each custom item's index into the full value list
  value.forEach((v, index) => {
    if (options.find((opt) => common.getOptionValue(opt) === common.getOptionValue(v))) {
      selectValue.push(v);
    } else {
      editItems.push({ item: v, index });
    }
  });

  return (
    <CellDropdown trapEnter onRequestClose={onClose}>
      {options.length > 0 ? <Select options={options} value={selectValue} onSelect={onSelect} {...common} /> : null}
      <Edit items={editItems} onAdd={onAdd} onUpdate={onUpdate} {...common} autoFocus={options.length === 0} />
    </CellDropdown>
  );
}
